import { createContext, useContext, useState } from 'react';
import type { DialogConfig, DialogContextType } from '@gokrew/models/dialog';

/**
 * DialogContext is a context that provides functions to open and close dialogs.
 */
const DialogContext = createContext<DialogContextType | undefined>(undefined);

/**
 * A component that provides a context to open and close dialogs.
 *
 * The DialogProvider should be wrapped around the root of your application, typically
 * at the top level of your App component.
 *
 * The DialogProvider provides the following functions in its context:
 * - openDialog: A function that opens a dialog and returns its id.
 * - closeDialog: A function that closes a dialog by its id.
 * - closeAll: A function that closes all open dialogs.
 */
export function DialogProvider({ children }: { children: React.ReactNode }) {
  const [dialogs, setDialogs] = useState<DialogConfig[]>([]);

  const openDialog = (config: Omit<DialogConfig, 'id'>): string => {
    const id = `modal-${Date.now()}-${Math.random()
      .toString(36)
      .substring(2, 11)}`;
    const newModal: DialogConfig = { ...config, id };
    setDialogs((prev) => [...prev, newModal]);
    return id;
  };

  /**
   * Replace part of an open dialog's config, so a dialog can swap its own
   * title or content in place instead of closing and reopening.
   */
  const updateDialog = (
    id: string,
    config: Partial<Omit<DialogConfig, 'id'>>
  ) => {
    setDialogs((prev) =>
      prev.map((modal) => (modal.id === id ? { ...modal, ...config } : modal))
    );
  };

  const closeDialog = (id: string) => {
    setDialogs((prev) => prev.filter((modal) => modal.id !== id));
  };

  /**
   * Close all dialogs
   *
   * This function is used to close all open dialogs. It is particularly useful in scenarios
   * where the user is logged out due to an error, and all of the dialogs need to be closed
   * immediately. This function ensures that all dialogs are closed, providing a clean
   * user experience.
   */
  const closeAll = () => setDialogs([]);

  return (
    <DialogContext.Provider
      value={{ openDialog, updateDialog, closeDialog, dialogs, closeAll }}
    >
      {children}
    </DialogContext.Provider>
  );
}

/**
 * A hook that returns the DialogContext.
 *
 * The DialogContext provides the following functions:
 * - openDialog: A function that opens a dialog and returns its id.
 * - closeDialog: A function that closes a dialog by its id.
 * - closeAll: A function that closes all open dialogs.
 *
 * The useDialog hook can only be used within a DialogProvider.
 *
 * @returns The DialogContext
 * @throws {Error} If the hook is used outside of a DialogProvider
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useDialog(): DialogContextType {
  const context = useContext(DialogContext);
  if (!context)
    throw new Error('useModal must be used within a DialogProvider');
  return context;
}
